import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const skeletonVariants = cva(
  'bg-gradient-to-r from-muted via-muted-foreground/20 to-muted bg-[length:200%_100%] animate-shimmer',
  {
    variants: {
      variant: {
        default: 'rounded-md',
        circle: 'rounded-full',
        text: 'rounded-sm',
        rect: 'rounded-none',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {
  variant?: 'default' | 'circle' | 'text' | 'rect';
}

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(skeletonVariants({ variant }), className)}
      {...props}
    />
  )
);

Skeleton.displayName = 'Skeleton';

const CircleAvatar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { size?: 'sm' | 'md' | 'lg' }
>(({ className, size = 'md', ...props }, ref) => {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
  };

  return (
    <Skeleton
      ref={ref}
      variant="circle"
      className={cn(sizeClasses[size], className)}
      {...props}
    />
  );
});

CircleAvatar.displayName = 'CircleAvatar';

const TextLine = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { width?: string }
>(({ className, width = 'w-full', ...props }, ref) => (
  <Skeleton
    ref={ref}
    variant="text"
    className={cn('h-4', width, className)}
    {...props}
  />
));

TextLine.displayName = 'TextLine';

const Heading = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { width?: string; size?: 'sm' | 'md' | 'lg' }
>(({ className, width = 'w-3/4', size = 'md', ...props }, ref) => {
  const sizeClasses = {
    sm: 'h-5',
    md: 'h-7',
    lg: 'h-9',
  };

  return (
    <Skeleton
      ref={ref}
      variant="text"
      className={cn(sizeClasses[size], width, className)}
      {...props}
    />
  );
});

Heading.displayName = 'Heading';

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-lg border border-border p-4 space-y-4', className)}
      {...props}
    >
      <div className="space-y-2">
        <Heading size="sm" width="w-3/4" />
        <TextLine width="w-full" />
        <TextLine width="w-5/6" />
      </div>
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-10 w-20 rounded-md" />
        <Skeleton className="h-10 flex-grow rounded-md" />
      </div>
    </div>
  )
);

Card.displayName = 'Card';

const EventCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { withImage?: boolean }
>(({ className, withImage = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('rounded-lg border border-border overflow-hidden', className)}
    {...props}
  >
    {withImage && <Skeleton className="h-40 w-full rounded-none" />}
    <div className="p-5 space-y-4">
      <Skeleton className="h-6 w-20 rounded-full" />
      <div className="space-y-2">
        <Heading size="sm" width="w-4/5" />
        <TextLine width="w-full" />
      </div>
      <div className="space-y-2">
        <TextLine width="w-3/5" />
        <TextLine width="w-2/5" />
      </div>
      <Skeleton className="h-10 w-full rounded-md" />
    </div>
  </div>
));

EventCard.displayName = 'EventCard';

const LeaderboardRow = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-4 px-4 py-3 border-b border-border', className)}
      {...props}
    >
      <CircleAvatar size="sm" />
      <div className="flex-grow space-y-2">
        <TextLine width="w-3/4" />
        <TextLine width="w-2/4" />
      </div>
      <div className="space-y-2 text-right">
        <TextLine width="w-16 ml-auto" />
        <TextLine width="w-12 ml-auto" />
      </div>
      <TextLine width="w-20" />
    </div>
  )
);

LeaderboardRow.displayName = 'LeaderboardRow';

const SkeletonList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    count?: number;
    variant?: 'text' | 'card' | 'row';
  }
>(({ className, count = 3, variant = 'row', ...props }, ref) => {
  const renderSkeleton = (index: number) => {
    switch (variant) {
      case 'text':
        return (
          <div key={index} className="space-y-2">
            <TextLine />
            <TextLine width="w-5/6" />
          </div>
        );
      case 'card':
        return <Card key={index} />;
      case 'row':
      default:
        return <LeaderboardRow key={index} />;
    }
  };

  return (
    <div ref={ref} className={cn('space-y-4', className)} {...props}>
      {Array.from({ length: count }).map((_, index) => renderSkeleton(index))}
    </div>
  );
});

SkeletonList.displayName = 'SkeletonList';

export {
  Skeleton,
  CircleAvatar,
  TextLine,
  Heading,
  Card,
  EventCard,
  LeaderboardRow,
  SkeletonList,
};
